import { useUpdateSurfacePreferences } from "@/components/preferences/usePreferences.tsx";
import { Button } from "@/components/ui/button.tsx";
import { errorToast } from "@/components/ui/toast.tsx";
import { cn } from "@/lib/utils.ts";
import {
  KEYBOARD_BINDING_MAX_COMBOS,
  type KeyboardShortcutId,
  keyboardShortcutIds,
} from "@/types/keyboardShortcuts.ts";
import { recordOf } from "@/types/utils.ts";
import * as Sentry from "@sentry/react";
import { Plus, RotateCcw, X } from "lucide-react";
import { useCallback } from "react";
import {
  isShortcutCustomized,
  resolveShortcutKeys,
  type ShortcutBindings,
  useShortcutBindings,
} from "./bindings.ts";
import { findShortcutConflicts } from "./conflicts.ts";
import { type FormattedCombo, formatCombo } from "./formatKeys.ts";
import { isReservedCombo } from "./reserved.ts";
import { ALL_SHORTCUTS, SHORTCUT_GROUPS, type ShortcutDef } from "./shortcuts.ts";
import { useShortcutRecorder } from "./useShortcutRecorder.ts";

type BindingValue = string[] | null;
type OnChangeBinding = (id: KeyboardShortcutId, value: BindingValue) => Promise<void>;

const ShortcutKey = ({ label }: { label: string }) => (
  <kbd className="inline-flex items-center justify-center min-w-[1.75rem] h-7 px-1.5 rounded-lg bg-muted/80 border border-border/60 text-xs font-mono text-muted-foreground">
    {label}
  </kbd>
);

/** One key combo as keycaps: a chord side by side, a sequence as "G then C". */
export const ShortcutCombo = ({ combo }: { combo: FormattedCombo }) => (
  <span className="inline-flex items-center gap-0.5">
    {combo.tokens.map((token, i) => (
      <span key={`${token}-${i}`} className="inline-flex items-center gap-0.5">
        {combo.sequence && i > 0 && (
          <span className="text-[0.625rem] text-muted-foreground/60 px-0.5">then</span>
        )}
        <ShortcutKey label={token} />
      </span>
    ))}
  </span>
);

/** The combo as words, for labels and warnings: "⌘ K", "G then C". */
const describeCombo = (combo: string): string => {
  const formatted = formatCombo(combo);
  return formatted.tokens.join(formatted.sequence ? " then " : " ");
};

const rowControlClass = "size-7 text-muted-foreground/70 hover:text-foreground";

/**
 * One bound combo in a row, with a remove control when the row is editable.
 * Removing the last chip leaves the shortcut off rather than restoring the
 * default; the row's reset control is the way back.
 */
const BindingChip = ({ combo, onRemove }: { combo: string; onRemove?: () => void }) => (
  <span className="inline-flex items-center">
    <ShortcutCombo combo={formatCombo(combo)} />
    {onRemove && (
      <Button
        variant="ghost"
        size="action"
        aria-label={`Remove ${describeCombo(combo)}`}
        title="Remove"
        onClick={onRemove}
        className={cn(rowControlClass, "size-5 ml-0.5")}
        data-statsig-name="keyboard-shortcut-remove-key"
      >
        <X className="size-3" />
      </Button>
    )}
  </span>
);

const bindingWarnings = (def: ShortcutDef, keys: string[], bindings: ShortcutBindings): string[] =>
  keys.flatMap((combo) => {
    const notes: string[] = [];
    if (isReservedCombo(combo)) {
      notes.push(
        `${describeCombo(combo)} is usually taken by the browser and may not reach Okthx.`,
      );
    }
    const conflicts = findShortcutConflicts(def.id, combo, bindings);
    if (conflicts.length > 0) {
      notes.push(
        `${describeCombo(combo)} also triggers ${conflicts.map((c) => `“${c.label}”`).join(", ")}.`,
      );
    }
    return notes;
  });

/**
 * One shortcut: its label, its live keys as chips, and the controls to record
 * another key or reset to the default. A `fixed` shortcut renders read-only.
 * Warnings under the row name combos the browser usually claims and combos
 * that would also fire another shortcut live on the same screen.
 */
const BindingRow = ({
  def,
  bindings,
  onChange,
}: {
  def: ShortcutDef;
  bindings: ShortcutBindings;
  onChange: OnChangeBinding;
}) => {
  const keys = resolveShortcutKeys(def, bindings);
  const customized = isShortcutCustomized(def, bindings);
  const editable = !def.fixed;

  const recorder = useShortcutRecorder((combo) => {
    if (keys.includes(combo)) return;
    if (keys.length >= KEYBOARD_BINDING_MAX_COMBOS) {
      errorToast(`A shortcut can have at most ${KEYBOARD_BINDING_MAX_COMBOS} keys.`);
      return;
    }
    void onChange(def.id, [...keys, combo]);
  });

  const warnings = editable ? bindingWarnings(def, keys, bindings) : [];

  return (
    <div className="py-2 border-b border-border/40 last:border-b-0">
      {/* Two columns on every row: the label keeps the left, centred against
          however many lines the keycaps take, and the keycaps wrap within the
          right column, so a shortcut with many keys grows the row downward
          without pushing the row off the edge. */}
      <div className="flex items-center justify-between gap-4">
        {/* Capped at half the row and free to wrap, so on a narrow window the
            keycap column always keeps room for a whole combo; a combo cannot
            wrap internally, and the scroll region clips rather than scrolls. */}
        <span className="min-w-0 max-w-[50%] text-sm text-foreground">{def.label}</span>
        <div className="flex min-w-0 flex-1 flex-wrap items-center justify-end gap-1.5">
          {keys.length === 0 && !recorder.recording && (
            <span className="text-xs italic text-muted-foreground">Off</span>
          )}
          {keys.map((combo, i) => (
            <span key={combo} className="flex items-center gap-1.5">
              {i > 0 && <span className="text-xs text-muted-foreground/50">/</span>}
              <BindingChip
                combo={combo}
                onRemove={
                  editable
                    ? () =>
                        void onChange(
                          def.id,
                          keys.filter((k) => k !== combo),
                        )
                    : undefined
                }
              />
            </span>
          ))}
          {recorder.recording && (
            <span
              className="inline-flex items-center gap-1 text-xs text-primary"
              aria-live="polite"
            >
              {recorder.steps.length === 0 ? (
                "Press keys…"
              ) : (
                <>
                  <ShortcutCombo combo={formatCombo(recorder.steps.join(" "))} />
                  then…
                </>
              )}
            </span>
          )}
          {editable && !recorder.recording && (
            <Button
              variant="ghost"
              size="action"
              aria-label={`Add key for ${def.label}`}
              title="Add a key"
              onClick={recorder.start}
              className={rowControlClass}
              data-statsig-name="keyboard-shortcut-add-key"
            >
              <Plus className="size-3.5" />
            </Button>
          )}
          {editable && customized && (
            <Button
              variant="ghost"
              size="action"
              aria-label={`Reset ${def.label} to default`}
              title="Reset to default"
              onClick={() => void onChange(def.id, null)}
              className={rowControlClass}
              data-statsig-name="keyboard-shortcut-reset-key"
            >
              <RotateCcw className="size-3.5" />
            </Button>
          )}
        </div>
      </div>
      {warnings.length > 0 && (
        <ul className="mt-1 text-xs text-amber-600 dark:text-amber-500">
          {warnings.map((warning) => (
            <li key={warning}>{warning}</li>
          ))}
        </ul>
      )}
    </div>
  );
};

/** Whether any shortcut carries a stored binding. */
export const hasCustomBindings = (bindings: ShortcutBindings): boolean =>
  ALL_SHORTCUTS.some((def) => isShortcutCustomized(def, bindings));

const allUnset: Record<KeyboardShortcutId, null> = recordOf(keyboardShortcutIds, () => null);

/**
 * Every shortcut with its live keys, each editable in place: `+` records a
 * new key, `×` removes one (removing the last turns the shortcut off), and a
 * customized row offers a reset. Used by both the `?` overlay and Settings.
 */
export const ShortcutBindingsEditor = ({ className }: { className?: string }) => {
  const bindings = useShortcutBindings();
  const updateKeyboardPreferences = useUpdateSurfacePreferences("keyboard");

  const onChange = useCallback<OnChangeBinding>(
    async (id, value) => {
      try {
        // Only the edited id goes up. The server deep-merges, so sending the
        // rest of the map would let a tab holding stale bindings roll back a
        // change another tab had already saved.
        await updateKeyboardPreferences({ bindings: { [id]: value } });
      } catch (error) {
        Sentry.captureException(error, { tags: { action: "keyboard-shortcut-save" } });
        errorToast("Failed to save the shortcut. Please try again.");
      }
    },
    [updateKeyboardPreferences],
  );

  const resetAll = useCallback(async () => {
    try {
      await updateKeyboardPreferences({ bindings: allUnset });
    } catch (error) {
      Sentry.captureException(error, { tags: { action: "keyboard-shortcut-reset-all" } });
      errorToast("Failed to reset shortcuts. Please try again.");
    }
  }, [updateKeyboardPreferences]);

  return (
    <div className={cn("flex flex-col gap-5", className)}>
      <div className="flex items-start justify-between gap-4">
        <p className="text-xs text-muted-foreground">
          Click + on a shortcut and press the keys you want. Two keys in a row make a sequence (G
          then C). Esc cancels.
        </p>
        {hasCustomBindings(bindings) && (
          <Button
            variant="outline"
            size="xs"
            onClick={() => void resetAll()}
            className="shrink-0"
            data-statsig-name="keyboard-shortcuts-reset-all"
          >
            Reset all
          </Button>
        )}
      </div>
      {SHORTCUT_GROUPS.map((group) => {
        const visible = group.shortcuts.filter((s) => !s.hidden);
        if (visible.length === 0) return null;
        return (
          <div key={group.id}>
            <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">
              {group.title}
            </h3>
            <div className="flex flex-col">
              {visible.map((shortcut) => (
                <BindingRow
                  key={shortcut.id}
                  def={shortcut}
                  bindings={bindings}
                  onChange={onChange}
                />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};
